import { useMutation, useQueryClient } from 'react-query';
import { axios } from '@/lib/axios';
import { useNotificationStore } from '@/stores/notifications';
import storage from '@/utils/storage';
import { API_URL } from '@/config';

export const uploadFilesInFolder = async ({
  folder_id,
  projectId,
  folder_name,
  workflow_name,
  is_api_file = false,
  workflow_id,
  file,
}: {
  folder_id: string;
  projectId: string;
  folder_name: string;
  workflow_name: string;
  is_api_file?: boolean;
  workflow_id?: string;
  file: any;
}): Promise<any> => {
  const access_token = storage.getToken();
  if (!access_token) throw new Error('No access token');

  const formData = new FormData();
  formData.append('folder_id', folder_id);
  formData.append('folder_name', folder_name);
  formData.append('workflow_name', workflow_name);
  formData.append('is_api_file', String(is_api_file));
  if (workflow_id !== undefined) {
    formData.append('workflow_id', workflow_id);
  }
  formData.append('file', file);

  try {
    const res = await axios.post(`${API_URL}/v2/project/${projectId}/contents/file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        // Optional: track upload progress if needed
      },
    });
    return res.data;
  } catch (error: any) {
    console.error('failed to upload file', error);
    throw error;
  }
};

/**
 * React Query mutation hook for uploading workflow JSON files.
 */
export const useUploadFilesInFolder = (projectId: string, config = {}) => {
  const { addNotification } = useNotificationStore();
  const queryClient = useQueryClient();

  return useMutation<
    any,
    Error,
    {
      folder_id: string;
      projectId: string;
      folder_name: string;
      workflow_name: string;
      workflow_id: string;
      is_api_file?: boolean;
      file: File;
    }
  >(uploadFilesInFolder, {
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to upload file',
        message: error.message,
      });
    },
    onSuccess: (data) => {
      if (data) {
        addNotification({
          type: 'success',
          title: 'File uploaded successfully',
        });
        queryClient.invalidateQueries(['projectContent', projectId]);
      }
    },
    ...config,
  });
};

// Response example for successful upload
// {
//   "status": "success",
//   "data": {
//       "id": "e694ea64-977d-468c-880b-af8aca7519e6",
//       "name": "workflow-flux-lora-trainer-on-comfyui_api.json",
//       "content_type": "application/json",
//       "size": 15167,
//       "size_mb": 0.01,
//       "file_url": "https://api-dev-misc.s3.amazonaws.com/projects/a27775e4-1947-45d4-b51d-0d91bf169002/6280ec1a8f7546be8b835ea07081582e.json?AWSAccessKeyId=AKIA2UC27KWGIUE7PGXO&Signature=lHKxC4WfrcgvgU%2B483101BHYB2Y%3D&Expires=1753345576",
//       "workflow_id": "ce91a5b4-4d86-480b-8625-146a0c210c0c",
//       "workflow_name": "workflow-flux-lora-trainer-on-comfyui_api",
//       "project_id": "23b93ffa-bd88-414a-a063-a9d1070808f2",
//       "folder_id": "a27775e4-1947-45d4-b51d-0d91bf169002"
//   }
// }
